import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectFlightsListReport, resetFlightMessagesByConversationIdStatusFlags, getFlightMessagesByConversationId, selectGetFlightMessagesByConversationIdStatus } from '_redux/slices/total-reports/total-reports';
import CustomReportsTable from './custom-reports-table';
import styled from 'styled-components';
import { Colors, Spacing } from '_styles';
import { chatsMessagesFileHandler, processConversationsMessagesReportData } from './report-utils';
import { selectAirportLogo } from '_redux/slices/analytics/reviews';
import { CustomSpinner } from '_atoms';


// styled components
const Container = styled.div`
  padding: ${Spacing.SCALE_10}px;
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: ${Spacing.SCALE_32}px ;
  text-align: center;
`;

const Title = styled.h2`
  color: ${Colors.PRM_DARK_BLUE};
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  color: ${Colors.GRAY_DARK};
  font-size: 16px;
  margin: 0;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: ${Spacing.SCALE_24}px;
  margin-bottom: ${Spacing.SCALE_32}px;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: white;
  padding: ${Spacing.SCALE_10}px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  min-width: 180px;
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${Colors.PRM_BLUE};
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${Colors.GRAY_DARK};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ConversationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: ${Spacing.SCALE_24}px;
  margin-bottom: ${Spacing.SCALE_32}px;
`;

const ConversationCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: ${Spacing.SCALE_10}px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: ${Colors.PRM_LIGHT_BLUE};
  }
  
  ${props => props.isSelected && `
    border-color: ${Colors.PRM_BLUE};
    background: ${Colors.PRM_BLUE_ACCENT};
  `}
`;

const FlightHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.SCALE_16}px;
`;

const FlightNumber = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${Colors.PRM_DARK_BLUE};
`;

const FlightRoute = styled.div`
  font-size: 16px;
  color: ${Colors.PRM_BLUE};
  font-weight: 500;
  margin-bottom: ${Spacing.SCALE_16}px;
`;


const ParticipantsContainer = styled.div`
  display: flex;
  gap: ${Spacing.SCALE_8}px;
  flex-wrap: wrap;
`;

const ParticipantTag = styled.span`
  background: ${Colors.PRM_LIGHT_BLUE_BG};
  color: ${Colors.PRM_DARK_BLUE};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const MessageCount = styled.div`
  background: ${Colors.PRM_BLUE};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  margin-left: auto;
`;

const StatusBadge = styled.span`
  background: ${props => props.status === 'archived' ? Colors.GRAY_MEDIUM : Colors.DAA_GREEN};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: ${Spacing.SCALE_24}px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-top: ${Spacing.SCALE_32}px;
`;

const BackButton = styled.button`
  background: ${Colors.PRM_BLUE};
  color: white;
  border: none;
  padding: ${Spacing.SCALE_12}px ${Spacing.SCALE_24}px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: ${Spacing.SCALE_24}px;
  transition: background 0.3s ease;
  
  &:hover {
    background: ${Colors.PRM_DARK_BLUE};
  }
`;


const FlightsConvReports = ({ setExcelRows, setExcelColumns, excelRows, excelColumns }) => {

    const dispatch = useDispatch();
    const flightsListReport = useSelector(selectFlightsListReport);
    const airportLogo = useSelector(selectAirportLogo)
    const getFlightMessagesByConversationIdStatus = useSelector(selectGetFlightMessagesByConversationIdStatus)

    const [selectedConversation, setSelectedConversation] = useState(null);
    // calculate statistics
    const stats = useMemo(() => {
        if (!Array.isFullArray(flightsListReport)) return null;

        const totalConversations = flightsListReport.length;
        const totalMessages = flightsListReport.reduce((sum, conv) => sum + (conv.messageCount || 0), 0);
        const activeConversations = flightsListReport.filter(conv => conv.status !== 'archived').length;
        const archivedConversations = flightsListReport.filter(conv => conv.status === 'archived').length;

        return {
            "Total Conversations": totalConversations,
            "Total Messages": totalMessages,
            "Active Conversations": activeConversations,
            "Archived Conversations": archivedConversations
        };
    }, [flightsListReport]);

    // handle conversation selection
    const handleConversationClick = async (conversation) => {

        setSelectedConversation(conversation);

        const res = await dispatch(getFlightMessagesByConversationId({ conversationId: conversation._id }));
        if (res?.meta?.requestStatus === "fulfilled") {
            const data = processConversationsMessagesReportData(res.payload);
            chatsMessagesFileHandler(data, setExcelRows, setExcelColumns, airportLogo);
        }
    };

    // handle back button
    const handleBack = () => {
        setSelectedConversation(null);
        setExcelColumns(null);
        setExcelRows(null);
        dispatch(resetFlightMessagesByConversationIdStatusFlags());
    };

    // format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    if (selectedConversation) {
        return (
            <Container>
                <BackButton onClick={handleBack}>
                    ← Back to Conversations
                </BackButton>

                <Header>
                    <Title>Flight Conversation Messages</Title>
                    <Subtitle>
                        {(() => {
                            // parse the conversation ID to extract departure and arrival
                            const idParts = selectedConversation._id?.split('_') || [];
                            if (idParts.length >= 3) {
                                const departure = idParts[1]; // first part after flight number is origin
                                const arrival = idParts[2];   // Second part is destination
                                return `${selectedConversation.flightNumber} • ${departure} → ${arrival} - ${formatDate(selectedConversation.flightDate)}`;
                            }
                            // fallback to original format if ID parsing fails
                            return `${selectedConversation.flightNumber} • ${formatDate(selectedConversation.flightDate)} • ${selectedConversation.iata}`;
                        })()}
                    </Subtitle>
                </Header>

                <TableContainer>
                    {getFlightMessagesByConversationIdStatus === "loading"
                        ? <CustomSpinner />
                        : <CustomReportsTable data={excelRows} columns={excelColumns} />
                    }
                </TableContainer>
            </Container>
        );
    }

    return (
        <Container>
            {stats && (
                <StatsContainer>
                    {Object.entries(stats).map(([key, value]) => (
                        <StatCard key={key}>
                            <StatNumber>{value}</StatNumber>
                            <StatLabel>{key}</StatLabel>
                        </StatCard>
                    ))}
                </StatsContainer>
            )}

            <ConversationsGrid>
                {flightsListReport.map((conversation, index) => (
                    <ConversationCard
                        key={conversation._id || index}
                        onClick={() => handleConversationClick(conversation)}
                        isSelected={false}
                    >
                        <FlightHeader>
                            <FlightNumber>{conversation.flightNumber}</FlightNumber>
                            <StatusBadge status={conversation.status}>
                                {conversation.status}
                            </StatusBadge>
                        </FlightHeader>

                        <FlightRoute>
                            {(() => {
                                // parse the conversation ID to extract departure and arrival
                                const idParts = conversation._id?.split('_') || [];
                                if (idParts.length >= 3) {
                                    const departure = idParts[1]; // first part after flight number is origin
                                    const arrival = idParts[2];   // second part is destination
                                    return `${departure} → ${arrival} - ${formatDate(conversation.flightDate)}`;
                                }
                                // fallback to original format if ID parsing fails
                                return `${conversation.iata} • ${formatDate(conversation.flightDate)}`;
                            })()}
                        </FlightRoute>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <ParticipantsContainer>
                                {conversation.participants?.slice(0, 3).map((participant, idx) => (
                                    <ParticipantTag key={idx}>
                                        {participant.name} ({participant.type})
                                    </ParticipantTag>
                                ))}
                                {conversation.participants?.length > 3 && (
                                    <ParticipantTag>
                                        +{conversation.participants.length - 3} more
                                    </ParticipantTag>
                                )}
                            </ParticipantsContainer>

                            <MessageCount>
                                {conversation.messageCount || 0} messages
                            </MessageCount>
                        </div>
                    </ConversationCard>
                ))}
            </ConversationsGrid>
        </Container>
    );
};

export default FlightsConvReports;